import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpService } from '../../http.service';
import { buildPeriodOptions, granularityOf, PeriodOption } from '../agent-account/period-resolver';

// Agent Commission Overview — one read-only pane that reconciles, per agent, the streams agent
// commission is spread across: COMPUTED (agent_report) vs PAID (agent_wallet) vs manual
// ADJUSTMENTS vs REFERRAL (player_referral_wallet) vs live balance, with entry counts.
@Component({
  selector: 'app-agent-commission-overview',
  templateUrl: './agent-commission-overview.component.html',
  styleUrls: ['./agent-commission-overview.component.scss'],
})
export class AgentCommissionOverviewComponent implements OnInit {
  public periodOptions: PeriodOption[] = [];
  public selectedPeriodKey = '';
  public defaultPeriodKey = '';
  public filter: any = { from_date: '', to_date: '' };

  public rows: any[] = [];
  public summary: any = null;
  public config: any = null;
  public period: any = { from_date: '', to_date: '', label: '' };
  public loading = false;
  public company_uuid = '';

  // View filters
  public search = '';
  public viewMode: 'all' | 'earning' | 'mismatch' | 'negative' = 'all';

  constructor(private http: HttpService, private router: Router) {}

  async ngOnInit() {
    this.company_uuid = this.http.getStaticSelectedCompany();
    await this.initPeriodFilter();
    await this.load();
  }

  async initPeriodFilter() {
    const s: any = await this.http.post('/admin/setting/get');
    const setting = s?.agent_account_period || 'current_month';
    this.periodOptions = buildPeriodOptions(granularityOf(setting), 12, new Date());
    const preselect = this.periodOptions.find(o => o.key === setting) || this.periodOptions[0];
    this.defaultPeriodKey = preselect.key;
    this.selectedPeriodKey = preselect.key;
    this.filter.from_date = preselect.from_date;
    this.filter.to_date = preselect.to_date;
  }

  get selectedPeriodLabel(): string {
    const opt = this.periodOptions.find(o => o.key === this.selectedPeriodKey);
    return opt ? opt.label : 'Custom Range';
  }

  onPeriodChange(key: string) {
    if (key === 'custom') { this.selectedPeriodKey = key; return; }
    const opt = this.periodOptions.find(o => o.key === key);
    if (!opt) return;
    this.selectedPeriodKey = key;
    this.filter.from_date = opt.from_date;
    this.filter.to_date = opt.to_date;
    this.load();
  }

  search_() { this.selectedPeriodKey = 'custom'; this.load(); }

  reset() {
    const opt = this.periodOptions.find(o => o.key === this.defaultPeriodKey) || this.periodOptions[0];
    if (!opt) return;
    this.selectedPeriodKey = opt.key;
    this.filter.from_date = opt.from_date;
    this.filter.to_date = opt.to_date;
    this.search = '';
    this.viewMode = 'all';
    this.load();
  }

  async load() {
    this.loading = true;
    try {
      const res: any = await this.http.post('/admin/agent-commission/overview', {
        from_date: this.filter.from_date,
        to_date: this.filter.to_date,
        period_label: this.selectedPeriodLabel,
      });
      if (res?.status) {
        this.rows = res.rows || [];
        this.summary = res.summary || null;
        this.config = res.config || null;
        this.period = res.period || this.period;
      } else {
        this.rows = []; this.summary = null; this.config = null;
      }
    } finally {
      this.loading = false;
    }
  }

  get modeLabel(): string {
    const m = this.config?.mode;
    if (m === 'red_share') return 'Red-Share (wallet basis)';
    if (m === 'winloss') return 'Win/Loss basis';
    if (m === 'cashflow') return 'Cash-Flow basis (deposit − withdraw)';
    return '—';
  }

  get modeBadgeClass(): string {
    const m = this.config?.mode;
    if (m === 'red_share') return 'bg-danger';
    if (m === 'winloss') return 'bg-primary';
    return 'bg-secondary';
  }

  // Config health warnings the operator should know about (the "why are the numbers weird" knobs).
  get configWarnings(): string[] {
    const w: string[] = [];
    if (!this.config) return w;
    if ((this.config.mode === 'winloss') && !this.config.carry_recompute_safe) {
      w.push('Carry-forward recompute-safe is OFF — winloss carry can drift on re-runs.');
    }
    if (this.config.mode === 'red_share' && !this.config.floor_at_zero) {
      w.push('Red-share floor-at-zero is OFF — winning subtrees can produce negative (clawback) commission.');
    }
    return w;
  }

  get filteredRows(): any[] {
    let list = this.rows;
    if (this.viewMode === 'earning') list = list.filter(r => r.net_paid > 0);
    else if (this.viewMode === 'mismatch') list = list.filter(r => !r.reconciled);
    else if (this.viewMode === 'negative') list = list.filter(r => r.negative_balance);
    const q = (this.search || '').trim().toLowerCase();
    if (q) list = list.filter(r =>
      (r.agent_code || '').toLowerCase().includes(q) ||
      (r.agent_name || '').toLowerCase().includes(q));
    return list;
  }

  drill(r: any) {
    this.router.navigate(['/lo', this.company_uuid, 'referral-rolling-account', r.agent_code]);
  }

  openMember(r: any) {
    if (!r?.player_uuid) return;
    this.router.navigate(['/lo', this.company_uuid, 'wallet-topup-withdraw'], { queryParams: { player: r.player_uuid } });
  }
}
